import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import { exportSettingsAudit } from 'common/services/audit.service';
import { downloadLink } from 'common/utils/utils';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import moment from 'moment';
import { useMemo, useState } from 'react';
import useToast from 'components/hooks/useToast';
import { AUDIT_FORMAT_TYPES } from './ExportAuditLogs';

export const useExportAuditLogs = ({ teamId, isTeamSettings }) => {
  const toast = useToast();
  const [period, setPeriod] = useState(PERIODS.PAST_30_DAYS);
  const [formatType, setFormatType] = useState(AUDIT_FORMAT_TYPES.CSV);
  const [isLoading, setIsLoading] = useState(false);

  const shouldAddTeamIdToPayload = useMemo(() => {
    return teamId && isTeamSettings;
  }, [teamId, isTeamSettings]);

  const isPeriodLongerThanOneYear = useMemo(() => {
    const { after, before } = period;
    const diffInYears = moment(before).diff(moment(after), 'years', true);
    return diffInYears > 1;
  }, [period]);

  const isSaveEnabled = useMemo(() => {
    return !isLoading && !isPeriodLongerThanOneYear;
  }, [isPeriodLongerThanOneYear, isLoading]);

  const exportAuditLogs = async () => {
    setIsLoading(true);
    toast({
      variant: 'info',
      message:
        'Downloading the audit log might take a moment. Feel free to keep using the application.',
      autoHideDuration: 5000
    });

    try {
      const { after, before } = period;
      const now = moment().format(SEARCH_DATE_FORMAT);
      const fileName = `Linearb-Audit-Log-${now}.${formatType}`;
      const { url } = await exportSettingsAudit({
        format: formatType,
        from: after,
        to: before,
        ...(shouldAddTeamIdToPayload && { team_id: teamId })
      });

      downloadLink(url, fileName);
    } catch (error) {
      let message = 'Failed to export file';

      // Check if server timed out
      if (error?.response?.status === 504) {
        message =
          'The log file is too large to download and caused a timeout; please try narrowing the date range';
      }

      toast({ message, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    period,
    setPeriod,
    exportAuditLogs,
    formatType,
    setFormatType,
    isSaveEnabled,
    isPeriodLongerThanOneYear
  };
};
